import './DataGrid.scss';

import React, { useEffect, useState } from 'react';

import DataGridCell from '@components/DataGrid/DataGridCell';
import { ColumnSort } from '@constants/column-sort';
import { IDataQuerySortModel } from '@models/data-query';
import { IDataGridColumn } from './models';

interface IProps<T> {
  columns: IDataGridColumn<T>[];
  rows: T[];
  onSortingUpdated?: (sortModel: IDataQuerySortModel<T>) => void;
}

function DataGrid<T extends { [key: string]: any }>({ columns, rows, onSortingUpdated }: IProps<T>) {
  const [activeSortModel, setActiveSortModel] = useState<IDataQuerySortModel<T>>();

  useEffect(() => {
    if (activeSortModel && onSortingUpdated) {
      onSortingUpdated(activeSortModel);
    }
  }, [activeSortModel]);

  const toggleSorting = (currentSort: ColumnSort): ColumnSort => {
    return currentSort === ColumnSort.Ascending ? ColumnSort.Descending : ColumnSort.Ascending;
  };

  const handleSorting = (fieldName: keyof T) => {
    setActiveSortModel((currentSorting) => {
      return {
        fieldName,
        sort:
          currentSorting?.fieldName === fieldName ? toggleSorting(currentSorting.sort) : ColumnSort.Ascending,
      };
    });
  };

  return (
    <div className="modDataGrid" role="grid">
      <header className="modDataGrid__header" role="rowheader">
        {columns.map(({ fieldName, headerName, sortable, ...params }) => (
          <DataGridCell
            key={fieldName.toString()}
            type="columnheader"
            {...params}
            onClick={sortable && (() => handleSorting(fieldName))}>
            {headerName && headerName}{' '}
            {sortable && activeSortModel && activeSortModel.fieldName === fieldName && (
              <i
                className={`modIcon ${
                  activeSortModel.sort === ColumnSort.Ascending
                    ? 'modIcon--chevron-up'
                    : 'modIcon--chevron-down'
                }`}
              />
            )}
          </DataGridCell>
        ))}
      </header>
      <div className="modDataGrid__content" role="rowgroup">
        {rows.map((row, i) => (
          <div key={i} className="modDataGridRow" role="row">
            {columns.map(({ fieldName, formatter, ...params }) => (
              <DataGridCell key={fieldName.toString()} type="gridcell" {...params}>
                {(formatter && formatter(row, fieldName)) || row[fieldName]?.toString()}
              </DataGridCell>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default DataGrid;
